import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { BarChart3, HandCoins } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { api, formatApiErrorDetail } from "../../lib/api";
import { inputCls, PageHeader } from "./shared";

const fmt = (n) => `₹${(n || 0).toLocaleString("en-IN")}`;
const d = (dt) => dt.toISOString().split("T")[0];

const presets = () => {
  const now = new Date();
  return {
    today: { from: d(now), to: d(now), label: "Today" },
    month: { from: d(new Date(now.getFullYear(), now.getMonth(), 1)), to: d(now), label: "This Month" },
    year: { from: d(new Date(now.getFullYear(), 0, 1)), to: d(now), label: "This Year" },
  };
};

export default function Reports() {
  const p = presets();
  const [range, setRange] = useState({ from: p.month.from, to: p.month.to });
  const [active, setActive] = useState("month");
  const [report, setReport] = useState(null);
  const [commissions, setCommissions] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [paying, setPaying] = useState(null);
  const [payForm, setPayForm] = useState({ method: "Cash", note: "" });

  const loadPayouts = useCallback(() => {
    api.get("/commissions/payouts").then(({ data }) => setPayouts(data)).catch(() => {});
  }, []);

  const load = useCallback((from, to) => {
    api.get("/reports/staff-revenue", { params: { from_date: from, to_date: to } })
      .then(({ data }) => setReport(data)).catch(() => {});
    api.get("/commissions/summary", { params: { from_date: from, to_date: to } })
      .then(({ data }) => setCommissions(data)).catch(() => {});
  }, []);

  useEffect(() => { load(range.from, range.to); loadPayouts(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const applyPreset = (key) => {
    const pr = presets()[key];
    setActive(key);
    setRange({ from: pr.from, to: pr.to });
    load(pr.from, pr.to);
  };

  const applyCustom = () => {
    if (!range.from || !range.to) return;
    setActive("custom");
    load(range.from, range.to);
  };

  const recordPayout = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/commissions/payout", {
        staff_id: paying.staff_id, from_date: range.from, to_date: range.to,
        method: payForm.method, note: payForm.note,
      });
      toast.success(`Paid ${fmt(data.amount)} to ${data.staff_name} — logged as a Salaries expense in Finance`);
      setPaying(null);
      setPayForm({ method: "Cash", note: "" });
      load(range.from, range.to);
      loadPayouts();
    } catch (e2) { toast.error(formatApiErrorDetail(e2.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <PageHeader title="Staff Revenue & Commissions" subtitle="Who earned what — and pay commissions in one tap." />
      <div className="flex flex-wrap items-center gap-3">
        {Object.entries(presets()).map(([key, pr]) => (
          <button key={key} onClick={() => applyPreset(key)} data-testid={`report-preset-${key}`}
            className={`text-xs font-semibold px-4 py-2 rounded-full transition-colors ${
              active === key ? "bg-[#0A3F2F] text-white" : "bg-white border border-[#E5E7E5] text-[#5C6A63] hover:border-[#0A3F2F]"
            }`}>
            {pr.label}
          </button>
        ))}
        <div className="flex items-center gap-2 ml-2">
          <input data-testid="report-from-input" type="date" className={`${inputCls} w-40`} value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} />
          <span className="text-[#5C6A63] text-sm">to</span>
          <input data-testid="report-to-input" type="date" className={`${inputCls} w-40`} value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} />
          <button onClick={applyCustom} data-testid="report-apply-btn"
            className="bg-[#C5A059] text-[#1C1C1C] text-xs font-bold px-4 py-2.5 rounded-full hover:bg-[#d4b06c]">Apply</button>
        </div>
      </div>
      {report && (
        <>
          <div className="bg-[#0A3F2F] text-white rounded-md p-5 flex items-center justify-between" data-testid="report-grand-total">
            <div className="flex items-center gap-3">
              <BarChart3 className="w-6 h-6 text-[#C5A059]" />
              <div>
                <p className="text-xs uppercase tracking-wider text-white/60">Total Staff Revenue</p>
                <p className="text-2xl font-bold text-[#C5A059]">{fmt(report.grand_total)}</p>
              </div>
            </div>
            <p className="text-xs text-white/60">{report.from_date} → {report.to_date}</p>
          </div>
          <div className="bg-white border border-[#E5E7E5] rounded-md shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E5E7E5] text-left text-xs uppercase tracking-wider text-[#5C6A63]">
                  <th className="px-4 py-3 font-semibold">Staff</th>
                  <th className="px-4 py-3 font-semibold">Role</th>
                  <th className="px-4 py-3 font-semibold">Services Done</th>
                  <th className="px-4 py-3 font-semibold">Billing Revenue</th>
                  <th className="px-4 py-3 font-semibold">Appointment Revenue</th>
                  <th className="px-4 py-3 font-semibold">Total</th>
                </tr>
              </thead>
              <tbody data-testid="staff-revenue-table">
                {report.rows.map((r) => (
                  <tr key={r.staff_id || "unassigned"} className="border-b border-[#E5E7E5] last:border-0 hover:bg-[#FAFAF8]"
                    data-testid={`staff-revenue-row-${r.staff_id || "unassigned"}`}>
                    <td className="px-4 py-3 font-semibold text-[#1C1C1C]">{r.name}</td>
                    <td className="px-4 py-3 text-[#5C6A63]">{r.role}</td>
                    <td className="px-4 py-3">{r.services}</td>
                    <td className="px-4 py-3">{fmt(r.bill_revenue)}</td>
                    <td className="px-4 py-3">{fmt(r.appointment_revenue)}</td>
                    <td className="px-4 py-3 font-bold text-[#0A3F2F]">{fmt(r.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {commissions && (
        <div className="space-y-3" data-testid="commissions-section">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold text-[#1C1C1C]">Commission Payouts — {commissions.from_date} → {commissions.to_date}</h2>
            {commissions.total_due > 0 && (
              <span className="text-xs font-bold text-red-600" data-testid="commission-total-due">Due to staff: {fmt(commissions.total_due)}</span>
            )}
          </div>
          <div className="bg-white border border-[#E5E7E5] rounded-md shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E5E7E5] text-left text-xs uppercase tracking-wider text-[#5C6A63]">
                  <th className="px-4 py-3 font-semibold">Staff</th>
                  <th className="px-4 py-3 font-semibold">Rate</th>
                  <th className="px-4 py-3 font-semibold">Services</th>
                  <th className="px-4 py-3 font-semibold">Commission Earned</th>
                  <th className="px-4 py-3 font-semibold">Paid</th>
                  <th className="px-4 py-3 font-semibold">Due</th>
                  <th className="px-4 py-3 font-semibold">Action</th>
                </tr>
              </thead>
              <tbody data-testid="commissions-table">
                {commissions.rows.map((r) => (
                  <tr key={r.staff_id} className="border-b border-[#E5E7E5] last:border-0 hover:bg-[#FAFAF8]" data-testid={`commission-row-${r.staff_id}`}>
                    <td className="px-4 py-3 font-semibold text-[#1C1C1C]">{r.name}<p className="text-xs font-normal text-[#5C6A63]">{r.role}</p></td>
                    <td className="px-4 py-3">{r.commission_rate}%</td>
                    <td className="px-4 py-3">{r.services}</td>
                    <td className="px-4 py-3 font-semibold">{fmt(r.earned)}</td>
                    <td className="px-4 py-3 text-emerald-700">{fmt(r.paid)}</td>
                    <td className="px-4 py-3 font-bold" data-testid={`commission-due-${r.staff_id}`}>
                      {r.due > 0 ? <span className="text-red-600">{fmt(r.due)}</span> : <span className="text-emerald-700">Settled</span>}
                    </td>
                    <td className="px-4 py-3">
                      {r.due > 0 && (
                        <button onClick={() => { setPaying(r); setPayForm({ method: "Cash", note: "" }); }} data-testid={`pay-commission-${r.staff_id}`}
                          className="flex items-center gap-1.5 bg-[#0A3F2F] text-white text-xs font-bold px-3.5 py-1.5 rounded-full hover:bg-[#0d5540]">
                          <HandCoins className="w-3.5 h-3.5" /> Pay {fmt(r.due)}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {payouts.length > 0 && (
            <div className="bg-white border border-[#E5E7E5] rounded-md shadow-sm overflow-x-auto">
              <p className="px-4 pt-4 text-xs font-bold uppercase tracking-wider text-[#5C6A63]">Payout History</p>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#E5E7E5] text-left text-xs uppercase tracking-wider text-[#5C6A63]">
                    <th className="px-4 py-3 font-semibold">Date</th>
                    <th className="px-4 py-3 font-semibold">Staff</th>
                    <th className="px-4 py-3 font-semibold">Period</th>
                    <th className="px-4 py-3 font-semibold">Amount</th>
                    <th className="px-4 py-3 font-semibold">Method</th>
                    <th className="px-4 py-3 font-semibold">Note</th>
                  </tr>
                </thead>
                <tbody data-testid="payouts-table">
                  {payouts.map((po) => (
                    <tr key={po.id} className="border-b border-[#E5E7E5] last:border-0 hover:bg-[#FAFAF8]" data-testid={`payout-row-${po.id}`}>
                      <td className="px-4 py-3">{po.date}</td>
                      <td className="px-4 py-3 font-semibold">{po.staff_name}</td>
                      <td className="px-4 py-3 text-[#5C6A63]">{po.from_date} → {po.to_date}</td>
                      <td className="px-4 py-3 font-bold text-[#0A3F2F]">{fmt(po.amount)}</td>
                      <td className="px-4 py-3">{po.method}</td>
                      <td className="px-4 py-3 text-[#5C6A63]">{po.note || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <Dialog open={!!paying} onOpenChange={(v) => !v && setPaying(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Pay Commission — {paying?.name}</DialogTitle>
            <DialogDescription>Records the payout and logs it as a Salaries expense automatically.</DialogDescription>
          </DialogHeader>
          <form onSubmit={recordPayout} className="space-y-4" data-testid="payout-form">
            <div className="bg-[#FAFAF8] rounded-md p-4 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-[#5C6A63]">Period</span><span>{range.from} → {range.to}</span></div>
              <div className="flex justify-between"><span className="text-[#5C6A63]">Services</span><span>{paying?.services}</span></div>
              <div className="flex justify-between font-bold text-[#0A3F2F]"><span>Amount to pay</span><span data-testid="payout-amount">{fmt(paying?.due)}</span></div>
            </div>
            <select data-testid="payout-method-select" className={inputCls} value={payForm.method} onChange={(e) => setPayForm({ ...payForm, method: e.target.value })}>
              <option>Cash</option><option>UPI</option><option>Bank Transfer</option><option>Cheque</option>
            </select>
            <input data-testid="payout-note-input" className={inputCls} placeholder="Note (optional)" value={payForm.note} onChange={(e) => setPayForm({ ...payForm, note: e.target.value })} />
            <p className="text-[11px] text-[#5C6A63]">This is also recorded automatically as a Salaries expense in Finance.</p>
            <button type="submit" data-testid="payout-submit-btn" className="w-full bg-[#0A3F2F] text-white font-semibold py-2.5 rounded-full text-sm hover:bg-[#0d5540]">
              Record Payout
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
